import React, { useState, useEffect } from "react";
import { Calculator, Layers, ShieldCheck, Sparkles, Plus, X } from "lucide-react";
import { Status, SectionHead, Toggle } from "../igualdad-components.jsx";
import { apiGet, apiPut, apiPost, apiDelete } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Configuración
   4 pestañas: parámetros de cálculo, conceptos, roles, IA
   ============================================================ */

const TABS = [["calc", "Parámetros de cálculo"], ["conceptos", "Conceptos retributivos"], ["roles", "Roles y permisos"], ["ia", "IA y protección de datos"]];
const ROLE_DOCS = [
  ["administrador", "Acceso total, configuración, usuarios y plantillas."],
  ["responsable_proyecto", "Crear clientes, gestionar proyectos y validar informes."],
  ["consultor", "Cargar datos, analizar, redactar comentarios y generar informes."],
  ["tecnico_datos", "Importar, depurar y validar información."],
  ["cliente_rrhh", "Subir documentación, revisar datos y responder tareas."],
  ["direccion_cliente", "Ver informes ejecutivos."],
  ["rlt_cliente", "Acceso restringido a documentación autorizada."],
  ["auditoria_interna", "Solo lectura y trazabilidad."],
];

const ROLE_KEYS = ROLE_DOCS.map(([k]) => k);

function IgualdadConfiguracion() {
  const { session, setPolitica, fireToast } = useIgualdad();

  const [tab, setTab] = useState("calc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conceptos, setConceptos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ajustes, setAjustes] = useState(null);
  const [calc, setCalc] = useState({});
  const [savingCalc, setSavingCalc] = useState(false);

  const [userModal, setUserModal] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userErr, setUserErr] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", password_confirmation: "", rol: "consultor", cliente_id: "", activo: true });

  // El origen lee `session.politica` en el momento de la llamada; aquí la
  // política llega por parámetro porque `setPolitica` es un dispatch.
  function initCalc(politica) {
    const p = politica || {};
    setCalc({
      umbral_alerta: p.umbral_alerta, umbral_ue: p.umbral_ue, formula_brecha: p.formula_brecha,
      dias_computables: p.dias_computables, jornada_completa_horas: p.jornada_completa_horas,
      equiparar_jornada: !!p.equiparar_jornada, excluir_indemnizaciones: !!p.excluir_indemnizaciones,
    });
  }

  async function load() {
    if (!session.politicaId) { setLoading(false); return; }
    setLoading(true); setError(null); initCalc(session.politica);
    try {
      const [c, u, a, cl] = await Promise.all([
        apiGet("/tipos-concepto", { politica_id: session.politicaId, per_page: 100 }),
        apiGet("/usuarios", { per_page: 100 }),
        apiGet("/ajustes-ia/" + session.politicaId),
        apiGet("/clientes", { per_page: 100 }),
      ]);
      setConceptos(c.data || []);
      setUsuarios(u.data || []);
      setAjustes(a.data);
      setClientes(cl.data || []);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }

  // Origen: onMounted(load) + watch(politicaId) — 2 disparadores, 1 efecto.
  useEffect(() => { load(); }, [session.politicaId]);

  async function saveCalc() {
    if (savingCalc) return;
    setSavingCalc(true);
    try {
      const res = await apiPut(`/politicas-retributivas/${session.politicaId}`, {
        umbral_alerta: Number(calc.umbral_alerta),
        umbral_ue: Number(calc.umbral_ue),
        dias_computables: Number(calc.dias_computables),
        jornada_completa_horas: Number(calc.jornada_completa_horas),
        equiparar_jornada: calc.equiparar_jornada,
        excluir_indemnizaciones: calc.excluir_indemnizaciones,
      });
      setPolitica(res.data);
      initCalc(res.data);
      fireToast("Parámetros de cálculo guardados.");
    } catch (e) {
      fireToast(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setSavingCalc(false); }
  }

  async function toggleConcepto(c, field, val) {
    const prev = c[field];
    setConceptos((list) => list.map((x) => (x.id === c.id ? { ...x, [field]: val } : x)));
    try {
      await apiPut(`/tipos-concepto/${c.id}`, { [field]: val });
      fireToast("Concepto actualizado.");
    } catch (e) {
      setConceptos((list) => list.map((x) => (x.id === c.id ? { ...x, [field]: prev } : x)));
      fireToast(e.message || "No se pudo actualizar.");
    }
  }

  async function saveAjuste(field, val) {
    const prev = ajustes[field];
    setAjustes((a) => ({ ...a, [field]: val }));
    try {
      await apiPut("/ajustes-ia/" + session.politicaId, { [field]: val });
      fireToast("Ajuste guardado.");
    } catch (e) {
      setAjustes((a) => ({ ...a, [field]: prev }));
      fireToast(e.message || "No se pudo guardar.");
    }
  }

  function openNewUser() {
    setUserForm({ name: "", email: "", password: "", password_confirmation: "", rol: "consultor", cliente_id: "", activo: true });
    setUserErr(null);
    setUserModal(true);
  }

  async function createUser() {
    if (savingUser) return;
    setSavingUser(true); setUserErr(null);
    try {
      await apiPost("/usuarios", {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        password_confirmation: userForm.password_confirmation,
        roles: [userForm.rol],
        cliente_id: userForm.cliente_id === "" ? null : Number(userForm.cliente_id),
        activo: userForm.activo,
      });
      setUserModal(false);
      fireToast("Usuario creado.");
      await load();
    } catch (e) {
      setUserErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo crear."));
    } finally { setSavingUser(false); }
  }

  async function toggleActivo(u) {
    if (togglingId) return;
    setTogglingId(u.id);
    const next = !u.activo;
    try {
      await apiPut(`/usuarios/${u.id}`, { activo: next });
      setUsuarios((list) => list.map((x) => (x.id === u.id ? { ...x, activo: next } : x)));
      fireToast(next ? "Usuario activado." : "Usuario desactivado.");
    } catch (e) { fireToast(e.message || "No se pudo actualizar."); }
    finally { setTogglingId(null); }
  }

  async function deleteUser() {
    if (deletingUser || !delUser) return;
    setDeletingUser(true);
    try {
      await apiDelete(`/usuarios/${delUser.id}`);
      fireToast("Usuario eliminado.");
      setDelUser(null);
      await load();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeletingUser(false); }
  }

  return (
    <div className="rb-page rb-conf">
      <h1 className="rb-h1">Configuración</h1>
      <div className="rb-tabs" style={{ marginBottom: 16 }}>{TABS.map(([k, l]) => <button key={k} className={"rb-tab" + (tab === k ? " is-active" : "")} onClick={() => setTab(k)}>{l}</button>)}</div>

      {(loading || error) ? <Status loading={loading} error={error} /> : (
        <>
          {tab === "calc" && (
            <div className="rb-card">
              <SectionHead
                icon={Calculator}
                title="Parámetros del registro retributivo"
                right={<button className="rb-btn" disabled={savingCalc} onClick={saveCalc}>{savingCalc ? "Guardando…" : "Guardar parámetros"}</button>}
              />
              <div className="rb-form">
                <div className="rb-field"><label>Umbral de alerta de brecha (%)</label><input className="rb-inp" type="number" step="0.01" value={calc.umbral_alerta ?? ""} onChange={(e) => setCalc((c) => ({ ...c, umbral_alerta: e.target.value }))} /><span className="rb-fieldnote">RD 902/2020 exige justificar diferencias ≥ 25 %.</span></div>
                <div className="rb-field"><label>Umbral Directiva UE (%)</label><input className="rb-inp" type="number" step="0.01" value={calc.umbral_ue ?? ""} onChange={(e) => setCalc((c) => ({ ...c, umbral_ue: e.target.value }))} /><span className="rb-fieldnote">Evaluación conjunta por encima del 5 % por categoría.</span></div>
                <div className="rb-field"><label>Fórmula de brecha</label><div className="rb-input">{calc.formula_brecha || "(♂ − ♀) / ♂ × 100"}</div></div>
                <div className="rb-field"><label>Días computables del ejercicio</label><input className="rb-inp" type="number" value={calc.dias_computables ?? ""} onChange={(e) => setCalc((c) => ({ ...c, dias_computables: e.target.value }))} /></div>
                <div className="rb-field"><label>Jornada completa de referencia (h/año)</label><input className="rb-inp" type="number" step="0.01" value={calc.jornada_completa_horas ?? ""} onChange={(e) => setCalc((c) => ({ ...c, jornada_completa_horas: e.target.value }))} /></div>
              </div>
              <div className="rb-togglerow"><div><b>Equiparar conceptos a jornada completa</b><p className="rb-fieldnote">Recomendado para comparar entre personas con distinta jornada.</p></div><Toggle on={calc.equiparar_jornada} onChange={(v) => setCalc((c) => ({ ...c, equiparar_jornada: v }))} /></div>
              <div className="rb-togglerow"><div><b>Excluir indemnizaciones y percepciones puntuales</b><p className="rb-fieldnote">Se excluyen del cálculo de brecha por defecto.</p></div><Toggle on={calc.excluir_indemnizaciones} onChange={(v) => setCalc((c) => ({ ...c, excluir_indemnizaciones: v }))} /></div>
            </div>
          )}

          {tab === "conceptos" && (
            <div className="rb-card rb-card-flat">
              <div style={{ padding: "14px 16px" }}><SectionHead icon={Layers} title="Clasificación de conceptos retributivos" /></div>
              <table className="rb-table"><thead><tr><th className="rb-l">Concepto</th><th className="rb-l">Clasificación</th><th>Anualizable</th><th>Computa total</th></tr></thead>
                <tbody>
                  {conceptos.map((c) => <tr key={c.id}><td className="rb-l rb-strong">{c.nombre}</td><td className="rb-l rb-soft">{c.clasificacion_label}</td><td className="rb-c"><Toggle on={c.anualizable} onChange={(v) => toggleConcepto(c, "anualizable", v)} /></td><td className="rb-c"><Toggle on={c.computa_total} onChange={(v) => toggleConcepto(c, "computa_total", v)} /></td></tr>)}
                  {!conceptos.length && <tr><td className="rb-l rb-soft" colSpan={4}>No hay conceptos definidos.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "roles" && (
            <div className="rb-card rb-card-flat">
              <div style={{ padding: "14px 16px" }}><SectionHead icon={ShieldCheck} title="Usuarios y roles" right={<button className="rb-btn-soft" onClick={openNewUser}><Plus size={14} /> Nuevo usuario</button>} /></div>
              <table className="rb-table"><thead><tr><th className="rb-l">Usuario</th><th className="rb-l">Correo</th><th className="rb-l">Roles</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr key={u.id}>
                      <td className="rb-l rb-strong">{u.name}</td>
                      <td className="rb-l rb-soft">{u.email}</td>
                      <td className="rb-l rb-soft">{(u.roles || []).join(", ") || (u.es_staff ? "staff" : "—")}</td>
                      <td><span className="rb-chip" style={{ background: u.activo ? "var(--success-bg)" : "var(--danger-bg)", color: u.activo ? "var(--success-700)" : "var(--danger-700)" }}>{u.activo ? "Activo" : "Inactivo"}</span></td>
                      <td className="rb-uactions">
                        <button className="rb-justb" disabled={togglingId === u.id} onClick={() => toggleActivo(u)}>{u.activo ? "Desactivar" : "Activar"}</button>
                        <button className="rb-justb rb-justb-danger" onClick={() => setDelUser(u)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                  {!usuarios.length && <tr><td className="rb-l rb-soft" colSpan={5}>No hay usuarios.</td></tr>}
                </tbody>
              </table>
              <div style={{ padding: "12px 16px" }}><p className="rb-fieldnote" style={{ marginBottom: 6 }}>Descripción de roles:</p>
                {ROLE_DOCS.map(([r, d]) => <div key={r} className="rb-cotarow"><span className="rb-mono rb-soft">{r}</span><span className="rb-soft" style={{ fontWeight: "var(--weight-regular)" }}>{d}</span></div>)}
              </div>
            </div>
          )}

          {tab === "ia" && ajustes && (
            <div className="rb-card">
              <SectionHead icon={Sparkles} title="Asistencia de IA y protección de datos" />
              <div className="rb-togglerow"><div><b>Igualdad Talention habilitada para este cliente</b><p className="rb-fieldnote">Sugerencias y borradores como apoyo al consultor, nunca decisión automática.</p></div><Toggle on={ajustes.ia_habilitada} onChange={(v) => saveAjuste("ia_habilitada", v)} /></div>
              <div className="rb-togglerow"><div><b>Marcar todo texto de IA como “borrador”</b><p className="rb-fieldnote">Requiere aprobación del consultor antes de incorporarlo a un informe.</p></div><Toggle on={ajustes.marcar_borrador} onChange={(v) => saveAjuste("marcar_borrador", v)} /></div>
              <div className="rb-togglerow"><div><b>Seudonimizar personas en el análisis</b><p className="rb-fieldnote">Oculta datos personales en pantallas y exportaciones.</p></div><Toggle on={ajustes.seudonimizar} onChange={(v) => saveAjuste("seudonimizar", v)} /></div>
              <div className="rb-togglerow"><div><b>No usar datos del cliente para entrenar modelos externos</b><p className="rb-fieldnote">Bloqueado por política de tratamiento de datos.</p></div><Toggle on={ajustes.no_entrenar_externos} onChange={(v) => saveAjuste("no_entrenar_externos", v)} /></div>
            </div>
          )}
        </>
      )}

      {userModal && (
        <div className="rb-modal-bg" onClick={() => setUserModal(false)}>
          <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Nuevo usuario</div><div className="rb-modal-s">Podrá iniciar sesión con el correo y la contraseña</div></div>
              <button className="rb-ico" onClick={() => setUserModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              <label>Nombre</label>
              <input className="rb-inp rb-inp-gap" value={userForm.name} onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nombre y apellidos" />
              <label>Correo electrónico</label>
              <input className="rb-inp rb-inp-gap" type="email" value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} placeholder="persona@empresa.com" />
              <label>Contraseña (mín. 8)</label>
              <input className="rb-inp rb-inp-gap" type="password" value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} autoComplete="new-password" />
              <label>Repetir contraseña</label>
              <input className="rb-inp rb-inp-gap" type="password" value={userForm.password_confirmation} onChange={(e) => setUserForm((f) => ({ ...f, password_confirmation: e.target.value }))} autoComplete="new-password" />
              <label>Rol</label>
              <select value={userForm.rol} onChange={(e) => setUserForm((f) => ({ ...f, rol: e.target.value }))}>{ROLE_KEYS.map((r) => <option key={r} value={r}>{r}</option>)}</select>
              <label>Cliente</label>
              <select value={userForm.cliente_id} onChange={(e) => setUserForm((f) => ({ ...f, cliente_id: e.target.value }))}><option value="">— Staff Talention (sin cliente) —</option>{clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select>
              <div className="rb-togglerow" style={{ marginTop: 10 }}><div><b>Activo</b><p className="rb-fieldnote">Solo los usuarios activos pueden iniciar sesión.</p></div><Toggle on={userForm.activo} onChange={(v) => setUserForm((f) => ({ ...f, activo: v }))} /></div>
              {userErr && <div className="rb-formerr">{userErr}</div>}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setUserModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={savingUser} onClick={createUser}>{savingUser ? "Creando…" : "Crear usuario"}</button>
            </div>
          </div>
        </div>
      )}

      {delUser && (
        <div className="rb-modal-bg" onClick={() => setDelUser(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar usuario</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setDelUser(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-fieldnote" style={{ fontSize: "var(--text-sm)" }}>¿Seguro que quieres eliminar a <b>{delUser.name}</b> ({delUser.email})?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setDelUser(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deletingUser} onClick={deleteUser}>{deletingUser ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { IgualdadConfiguracion };
